import React, { useMemo, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import startCase from 'lodash/startCase';
import styled, { css } from 'styled-components';
import { theme, toRem } from 'design';
import { routeConstants } from 'routes';
import { logOutRequest, selectCurrentEmployee, setCurrentEmployeeRequest } from 'store/App';
import { ICurrentEmployee, IEmployerState, IUserState } from 'store/types';
import { EmployeeType, IEmployerSite, SiteRole } from 'types';
import { Avatar } from '../Avatar';
import { Icon } from '../Icon';
import { HeaderDropdown } from './HeaderDropdown';

interface IHeaderProfileSwitcherProps {
  user: IUserState;
  employers: Record<string, IEmployerState>;
  isLoading: boolean;
  className?: string;
  isNavOpen?: boolean;
  onNavOpenChange?: () => void;
}

const Layout = styled.button<{ isOpen: boolean }>`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: auto;
  height: ${toRem(44)};
  border-radius: ${toRem(11)};
  transition: background-color ${theme.transition.button.fast};

  @media (min-width: ${theme.breakpoint.small}px) {
    padding: ${toRem(5.5)} ${toRem(16.5)} ${toRem(5.5)} ${toRem(5.5)};

    &:hover {
      background-color: rgba(255, 255, 255, 0.055);
    }
  }

  ${({ isOpen }) =>
    isOpen &&
    css`
      background-color: rgba(255, 255, 255, 0.055);
    `}
`;

const User = styled.div`
  position: relative;
  flex-grow: 1;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: ${toRem(11)};
`;

const UserInfo = styled.div`
  display: flex;
  position: relative;
  top: ${toRem(1.25)};
  flex-flow: column nowrap;
  justify-content: center;
  align-items: flex-start;
  height: 100%;
  font-family: ${theme.font.family.euclid};
  font-weight: ${theme.font.weight.semiBold};
  font-size: ${toRem(13.75)};
  line-height: 100%;
  color: ${theme.color.white};
  text-align: left;
`;

const Name = styled.div`
  position: relative;
  flex-flow: row nowrap;
  justify-content: flex-start;
  align-items: center;
  width: auto;
`;

const ActiveProfile = styled.span`
  margin-top: ${toRem(3)};
  font-weight: ${theme.font.weight.regular};
  font-size: ${toRem(11)};
`;

const IconCarrot = styled(Icon).attrs({ icon: 'Carrot', width: 6, height: 4, fill: theme.color.neutral[400] })`
  position: relative;
  top: ${toRem(-1)};
  margin-left: ${toRem(8.75)};
`;

const addressForSite = (site: IEmployerSite): string | undefined => site.address?.address1 && site.address?.city && `${site.address.address1}, ${site.address.city} ${site.address.state} ${site.address.zipCode}`;

const HeaderProfileSwitcher: React.FC<IHeaderProfileSwitcherProps> = ({ isLoading, user, employers, className, isNavOpen, onNavOpenChange }: IHeaderProfileSwitcherProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentEmployee = useSelector(selectCurrentEmployee());
  const currentEmployerOrg = employers?.[currentEmployee?.organizationId]; // Current Employee Organization
  const currentEmployerSite = currentEmployerOrg?.sites?.[currentEmployee?.siteId]; // Current Employee Site
  const [isOpen, setIsOpen] = useState(false);

  const handleProfileSwitcherChange = () => {
    setIsOpen(!isOpen);

    if (isNavOpen && onNavOpenChange) onNavOpenChange();
  };

  const activeProfileText = useMemo(
    () => (): string => {
      // handle organization admin select
      if (currentEmployerOrg && currentEmployerOrg.type === EmployeeType.ADMIN) {
        return `${startCase(currentEmployerOrg.name)}, Admin`;
      }

      // handle site admin/manager select
      if (currentEmployerOrg && currentEmployerSite && currentEmployerOrg.type === EmployeeType.USER) {
        return `${startCase(currentEmployerSite.name)}, ${startCase(currentEmployerSite.role)}`;
      }

      return '';
    },
    [currentEmployerOrg, currentEmployerSite]
  );

  // Build admin roles
  const adminRoleOrgs = useMemo(() => (employers ? Object.values(employers).filter((org) => org.type === EmployeeType.ADMIN) : []), [employers]);

  // Build management roles
  const managerRoleSites = useMemo(() => {
    const sites: any = [];
    const organizations = employers ? Object.values(employers).filter((org) => org.type === EmployeeType.USER) : [];

    organizations.forEach((org) =>
      Object.values(org.sites).forEach((site) => {
        if (site.role === SiteRole.ADMIN || site.role === SiteRole.MANAGER) {
          sites.push({ ...site, organizationId: org.id, address: addressForSite(site) });
        }
      })
    );

    return sites;
  }, [employers]);

  const handleNavigateToSettings = () => {
    setIsOpen(false);
    history.push(routeConstants.private.settingsAccount);
  };

  const handleOnSelect = (option: ICurrentEmployee) => {
    dispatch(setCurrentEmployeeRequest(option));

    // Redirect to root (but not if on Add DOB)
    if (history.location.pathname !== routeConstants.private.addDOB) history.push(routeConstants.private.rootPath);
  };

  const handleSelectSelf = () => {
    dispatch(setCurrentEmployeeRequest());
    history.push(routeConstants.private.rootPath);
  };

  return (
    <>
      <Layout isOpen={isOpen} className={className} onClick={handleProfileSwitcherChange} data-cy="header-profile-switcher">
        <User>
          <Avatar size="small" username={`${user?.firstName} ${user?.lastName}`} image={user?.avatarUrl} isLoading={isLoading} />
          {isLoading ? (
            <Skeleton width={100} />
          ) : (
            <UserInfo>
              <Name>
                {`${user?.firstName} ${user?.lastName}`}
                <IconCarrot />
              </Name>
              {activeProfileText() && <ActiveProfile data-cy="active-profile">{activeProfileText()}</ActiveProfile>}
            </UserInfo>
          )}
        </User>
      </Layout>
      <HeaderDropdown
        adminRoleOrgs={adminRoleOrgs}
        managerRoleOrgs={managerRoleSites}
        currentOption={currentEmployee}
        username={`${user?.firstName} ${user?.lastName}`}
        isVisible={isOpen}
        onClose={() => setIsOpen(!isOpen)}
        onSelectSelf={handleSelectSelf}
        onSelect={handleOnSelect}
        onSettings={handleNavigateToSettings}
        onLogout={() => dispatch(logOutRequest(`${routeConstants.public.login}?message=logged_out`))}
      />
    </>
  );
};

export default HeaderProfileSwitcher;
